import { DEFAULT_LOCATION_THRESHOLD_KM } from '../consts/index.js'
import type { LngLat } from '../primitives/geo.js'

export * from './zod.js'

export function helloWorld() {
  return 'HelloWorld `nomadhub` package'
}
export async function getExchangeRate(
  from: string[] | string,
  to: string[] | string
) {
  const fromSymbols = Array.isArray(from) ? from.join(',') : from
  const toSymbols = Array.isArray(to) ? to.join(',') : to
  const url = `https://min-api.cryptocompare.com/data/pricemulti?fsyms=${fromSymbols}&tsyms=${toSymbols}`

  try {
    const response = await fetch(url)
    if (!response.ok) {
      throw new Error(`Failed to fetch exchange rate: ${response.statusText}`)
    }
    const data = await response.json()
    return data
  } catch (error) {
    console.error('Error fetching exchange rate:', error)
    throw error
  }
}

export function supportEmailLink(
  email: string,
  subjectText: string,
  bodyText: string,
  userId?: string
) {
  const subject = encodeURIComponent(subjectText)
  const body = encodeURIComponent(
    userId ? `${bodyText} (User ID: ${userId})` : bodyText
  )
  return `mailto:${email}?subject=${subject}&body=${body}`
}
/**
 * Calculates the great-circle distance between two geographical points using the Haversine formula.
 * @param coords1 First coordinate point {lat, lng}
 * @param coords2 Second coordinate point {lat, lng}
 * @returns Distance in kilometers
 * @throws {Error} If coordinates are invalid
 */
export function haversineDistance(coords1: LngLat, coords2: LngLat) {
  if (!coords1 || !coords2) {
    throw new Error(
      'Invalid coordinates: coordinates cannot be null or undefined'
    )
  }

  if (
    !Number.isFinite(coords1.lat) ||
    !Number.isFinite(coords1.lng) ||
    !Number.isFinite(coords2.lat) ||
    !Number.isFinite(coords2.lng)
  ) {
    throw new Error(
      'Invalid coordinates: latitude and longitude must be finite numbers'
    )
  }

  if (Math.abs(coords1.lat) > 90 || Math.abs(coords2.lat) > 90) {
    throw new Error(
      'Invalid coordinates: latitude must be between -90 and 90 degrees'
    )
  }

  if (Math.abs(coords1.lng) > 180 || Math.abs(coords2.lng) > 180) {
    throw new Error(
      'Invalid coordinates: longitude must be between -180 and 180 degrees'
    )
  }

  const R = 6371
  const lat1 = coords1.lat * (Math.PI / 180)
  const lat2 = coords2.lat * (Math.PI / 180)
  const deltaLat = (coords2.lat - coords1.lat) * (Math.PI / 180)
  const deltaLng = (coords2.lng - coords1.lng) * (Math.PI / 180)

  const sinDeltaLat = Math.sin(deltaLat / 2)
  const sinDeltaLng = Math.sin(deltaLng / 2)
  const cosLat1 = Math.cos(lat1)
  const cosLat2 = Math.cos(lat2)

  const a =
    sinDeltaLat * sinDeltaLat + cosLat1 * cosLat2 * sinDeltaLng * sinDeltaLng

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return R * c
}

export function convertBigIntToString(value: any): any {
  if (typeof value === 'bigint') {
    return value.toString()
  }
  if (Array.isArray(value)) {
    return value.map(convertBigIntToString)
  }
  if (value !== null && typeof value === 'object') {
    const transformed = { ...value }
    Object.keys(transformed).forEach((key) => {
      transformed[key] = convertBigIntToString(transformed[key])
    })
    return transformed
  }
  return value
}

export async function isSameCity(
  userCoords: { lat: number; lng: number; name: string },
  eventCoords: { lat: number; lng: number; name: string }
): Promise<boolean> {
  try {
    if (
      !userCoords?.lat ||
      !userCoords?.lng ||
      !eventCoords?.lat ||
      !eventCoords?.lng
    ) {
      return false
    }

    if (userCoords.name && eventCoords.name) {
      const normalizedUserCity = userCoords.name.toLowerCase().trim()
      const normalizedEventCity = eventCoords.name.toLowerCase().trim()
      if (normalizedUserCity === normalizedEventCity) {
        return true
      }
    }

    return (
      haversineDistance(userCoords, eventCoords) <=
      DEFAULT_LOCATION_THRESHOLD_KM
    )
  } catch (error) {
    console.error('Error in isSameCity:', error)
    return false
  }
}
